// src/Dashboard.js
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./navigation";
import IosShareIcon from "@mui/icons-material/IosShare";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Pagination,
  Snackbar,
  Stack,
  Typography,
  useTheme,
  Chip,
  useMediaQuery,
  Link,
} from "@mui/material";
import NoticePreviewDialog from "./director/components/NoticePreviewDialog";
import { useLocationLabels } from "./hooks/useLocationLabels";
import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import Footer from "./Footer";
import PaginationControl from "./utils/PaginationControl";
import GlobalNotification from "./components/GlobalNotification";
import PageInstructions from "./talent/components/PageInstructions.js";
import { useVerifiedDirector } from "./utils/VerifiedDirectorContext";
import ShareMenu from "./utils/ShareMenu";

function Dashboard() {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  //const [user, setUser] = useState(null);
  const [notices, setNotices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [previewNotice, setPreviewNotice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openNotification, setOpenNotification] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [userId, setUserId] = useState(null);
  const userRole = localStorage.getItem("userRole");
  const pageSize = 10;
  const [anchorEl, setAnchorEl] = useState(null);
const [shareUrl, setShareUrl] = useState("");
const [shareNotice, setShareNotice] = useState(null);
const [toast, setToast] = useState({
  open: false,
  message: "",
  severity: "success",
});

const { verifiedDirector } = useVerifiedDirector();

  const navigate = useNavigate();
  const { loading: locLoading, formatLocations } = useLocationLabels();

  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("loggedInUser");
    return raw ? JSON.parse(raw) : null;
  });

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  useEffect(() => {
    const id = localStorage.getItem("userId");
    if (id) setUserId(id);
  }, []);

  // Fetch notifications
  useEffect(() => {
    
    if (!user) return;
    const userId = user.data.userId;
    (async () => {
      try {
        const res = await api.get(ENDPOINTS.getNotifications(), {
          params: { userId, userrole: "Director" },
        });
setNotifications(res.data?.data || []);
      } catch (_) {}
    })();
  }, [user]);
  const url = ENDPOINTS.getCastingNotices({
    email: localStorage.getItem("email"),
    page,
    pageSize,
  });


  useEffect(() => {
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await api.get(url);
        const payload = response.data || {};
        setTotalCount(payload.count || 0);
        const sorted = (payload.data || []).sort((a, b) => {
          const dateA = new Date(a.noticeUpdatedDate || a.noticeCreatedDate);
          const dateB = new Date(b.noticeUpdatedDate || b.noticeCreatedDate);
          return dateB - dateA;
        });
        setNotices(sorted);
      } catch (e) {
        setError("Failed to load projects");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [page]);

  const dismissNotification = async (notificationId) => {
    if (!user) return;
    const userId = user.data.userId;

    try {
      await api.post(ENDPOINTS.dismissNotification(), {
        userId,
        notificationId,
      });

      setNotifications((prev) =>
        prev.filter((n) => n.notificationId !== notificationId),
      );
    } catch (error) {
      console.error("Error dismissing notification:", error);
    }
  };

  const formatDateTime = (s) =>
    s
      ? new Date(s).toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A";
const showToast = (message, severity = "success") => {
  setToast({
    open: true,
    message,
    severity,
  });
};

const openShare = (e, notice) => {
  setAnchorEl(e.currentTarget);
  setShareNotice(notice);
  setShareUrl(
    `${window.location.origin}/casting-call/${notice.noticeId}`
  );
};
const closeShare = () => {
  setAnchorEl(null);
  setShareUrl("");
  setShareNotice(null);
};
  return (
    <>
      <Navbar />

      <Box sx={{ background: "#eeeeee", py: 3 }}>
        <Collapse in={openNotification && notifications.length > 0}>
          <Alert
            severity="info"
            sx={{ mb: 2, mx: "auto", maxWidth: 1200 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setOpenNotification(false);
                  if (notifications[0]?.id) {
                    dismissNotification(notifications[0].id);
                  }
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            <AlertTitle>{notifications[0]?.title || "Notification"}</AlertTitle>
            {notifications[0]?.message || "No message available."}
          </Alert>
        </Collapse>
        {!verifiedDirector && (
          <Box  sx={{ mb: 2, mx: "auto", maxWidth: 1200, px:3 }}>
          <Alert severity="info">
            <Typography fontWeight={700} sx={{ fontSize: "15px" }}>
              Welcome to your DirectSubmit Casting Account.
            </Typography>
            <br />
            To get started, post your first casting notice by clicking the
            <strong> Create New Casting Call </strong>
            button.
            <br />
            Once submitted, our staff will review your casting notice and
            contact you to walk you through the features of your account.
            <br />
            Please make sure the account information you submitted is accurate.
            You can update your information at any time on the{" "}
            <Link href="/account" underline="always" sx={{ fontWeight: 700 }}>
              Edit Account
            </Link>{" "}
            page.
            <br />
            You will be able to <strong>Search Talent</strong> once your first
            casting notice has been approved.
            <br />
            For urgent casting notice approval, please call us at
            <strong> 212.812.1441</strong>.
          </Alert>
          </Box>
        )}

        {/* Wrap everything in one Paper */}
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            p: 3,
            backgroundColor: "#eee",
          }}
        >
          <GlobalNotification userId={userId} userRole={userRole} />
          {/* Header */}
          <Grid container alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h4">My Projects</Typography>
            </Grid>
            <Grid item xs={12} sm={6} textAlign={isSmUp ? "right" : "left"}>
              <Button
                onClick={() => navigate("/post-casting-call")}
                disabled={isLoading}
              >
                Create New Casting Call
              </Button>
            </Grid>
          </Grid>

          {/* Loading / Error */}
          {isLoading || locLoading ? (
            <Box textAlign="center" py={5}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" align="center">
              {error}
            </Typography>
          ) : (
            <>
              {/* List */}
              {notices.map((n) => (
                <Paper
                  key={n.noticeId}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    position: "relative",
                  }}
                >
                  <Grid
                    container
                    spacing={2}
                    alignItems={isSmUp ? "center" : "stretch"}
                    direction={isSmUp ? "row" : "column"}
                  >
                    {/* Details */}
                    <Grid item xs>
                      <Typography
                        variant="h6"
                        component="span"
                        fontWeight={700}
                        onClick={() =>
                          navigate(`/post-casting-call/${n.noticeId}`, {
                            state: { casting: n, noticeId: n.noticeId },
                          })
                        }
                        sx={{ cursor: "pointer", textDecoration: "none" }} // optional styling
                      >
                        {n.projectTitle}
                        {n.noticeStatus === "Pending" && (
                          <Chip
                            label="Pending"
                            size="small"
                            sx={{
                              ml: 1,
                              verticalAlign: "middle",
                              backgroundColor: "#d17c7c",
                              color: "#fff", // optional: ensures text is readable
                            }}
                          />
                        )}
                        {n.noticeStatus === "Rejected" && (
                          <Chip
                            label="Rejected"
                            size="small"
                            color="error"
                            sx={{ ml: 1, verticalAlign: "middle" }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body2">
                        Location: {formatLocations(n.auditionLocations)}
                      </Typography>
                      <Typography variant="body2">
                        Deadline: {formatDateTime(n.noticeEndDate)}
                      </Typography>
                    </Grid>

                    {/* Actions */}
                    <Grid
                      item
                      xs={12}
                      sm="auto"
                      sx={{
                        display: "flex",
                        flexDirection: isSmUp ? "row" : "column",
                        gap: 1,
                        justifyContent: "flex-end",
                        width: isSmUp ? "auto" : "100%",
                        mt: isSmUp ? 0 : 2,
                      }}
                    >
                      <Button onClick={() => setPreviewNotice(n)}>View</Button>
                      <Button
                        onClick={() =>
                          navigate(`/post-casting-call/${n.noticeId}`, {
                            state: { casting: n, noticeId: n.noticeId },
                          })
                        }
                      >
                        Edit Casting
                      </Button>
                      <Button
                        onClick={() =>
                          navigate(`/talent-submission/${n.noticeId}`)
                        }
                      >
                        View Submissions
                      </Button>
                    </Grid>

                    {/* Share */}
                    <Grid
                      item
                      xs="auto"
                      sx={{
                        display: "flex",
                        justifyContent: isSmUp ? "flex-end" : "center",
                        width: isSmUp ? "auto" : "100%",
                        mt: isSmUp ? 0 : 1,
                      }}
                    >
                      <IconButton
    aria-label="Share"
    onClick={(e) => openShare(e, n)}
    size="small"
>
                        <svg
                          viewBox="0 0 512 512"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24" // or use style: width: 24px
                          height="24"
                        >
                          <path d="M295.204,180.593C132.168,180.593,0,312.759,0,475.796 c51.398-130.047,184.869-203.22,317.483-183.603L295.204,180.593z" />
                          <path d="M512,253L295.204,36.204v217.818C159.946,249.655,34.992,339.262,0,475.794 c59.905-109.171,178.724-165.463,295.204-151.033v145.035L512,253z" />
                        </svg>
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}

              <PaginationControl
                page={page}
                totalItems={totalCount}
                pageSize={pageSize}
                onPageChange={(newPage) => setPage(newPage)}
                sx={{ mt: 3 }}
              />
            </>
          )}
        </Box>

        {/* Preview Dialog */}
        <NoticePreviewDialog
          open={!!previewNotice}
          notice={previewNotice}
          onClose={() => setPreviewNotice(null)}
        />

        {/* Share Menu */}
   <ShareMenu
  anchorEl={anchorEl}
  open={Boolean(anchorEl)}
  onClose={closeShare}
  shareUrl={shareUrl}
  title={shareNotice?.projectTitle}
  pay={shareNotice?.payScale}
  location={shareNotice?.auditionLocations}
  deadline={shareNotice?.noticeEndDate}
  onCopySuccess={() => showToast("Link copied!", "success")}
/>
      </Box>
   <Snackbar
  open={toast.open}
  autoHideDuration={2000}
  anchorOrigin={{
    vertical: "bottom",
    horizontal: "center",
  }}
  onClose={() =>
    setToast((prev) => ({
      ...prev,
      open: false,
    }))
  }
>
  <Alert
    severity={toast.severity}
    variant="filled"
    onClose={() =>
      setToast((prev) => ({
        ...prev,
        open: false,
      }))
    }
  >
    {toast.message}
  </Alert>
</Snackbar>
      <Footer />
    </>
  );
}

export default Dashboard;
